import { useMemo } from 'react';
import { motion } from 'framer-motion';

const BLOBS = [
  { className: 'bg-cyan-500/20 top-[-10%] start-[-5%] size-[34rem]', x: 50, y: 30, duration: 22 },
  { className: 'bg-amber-400/10 top-[30%] end-[-10%] size-[30rem]', x: -60, y: 40, duration: 26 },
  { className: 'bg-violet-500/15 bottom-[-15%] start-[25%] size-[36rem]', x: 40, y: -50, duration: 30 },
];

/** Fixed, non-interactive layer: glowing grid, circuit traces, blobs, particles. */
export default function BackgroundEffects() {
  const particles = useMemo(
    () =>
      Array.from({ length: 26 }, (_, i) => ({
        left: `${(i * 37) % 100}%`,
        top: `${(i * 61) % 100}%`,
        size: 2 + (i % 3),
        duration: 7 + (i % 5) * 2.5,
        delay: (i % 6) * 0.9,
      })),
    [],
  );

  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      {/* Robotics lab backdrop, heavily darkened so content stays readable */}
      <img
        src="/assets/robotics lab.png"
        alt=""
        className="absolute inset-0 size-full object-cover opacity-50"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950/85 via-navy-950/70 to-navy-950" />

      {/* Glowing lab grid, fading out toward the edges */}
      <div className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_70%_60%_at_50%_35%,black,transparent)]" />

      {/* Slow-drifting neon blobs */}
      {BLOBS.map((blob, i) => (
        <motion.div
          key={i}
          className={`absolute rounded-full blur-3xl ${blob.className}`}
          animate={{ x: [0, blob.x, 0], y: [0, blob.y, 0] }}
          transition={{ duration: blob.duration, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}

      {/* Animated circuit traces along the bottom */}
      <svg
        className="absolute inset-x-0 bottom-0 h-[40vh] w-full text-cyan-400/20"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          className="circuit-line"
          d="M0 220 H 320 L 380 160 H 640 L 700 240 H 1030 L 1100 140 H 1440"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          className="circuit-line"
          style={{ animationDelay: '-3s' }}
          d="M0 290 H 240 L 300 250 H 760 L 820 300 H 1180 L 1240 230 H 1440"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.6"
        />
        <circle cx="380" cy="160" r="4" fill="currentColor" />
        <circle cx="700" cy="240" r="4" fill="currentColor" />
        <circle cx="1100" cy="140" r="4" fill="currentColor" />
      </svg>

      {/* Floating particles */}
      {particles.map((p, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-cyan-300/50"
          style={{ left: p.left, top: p.top, width: p.size, height: p.size }}
          animate={{ y: [0, -28, 0], opacity: [0.15, 0.7, 0.15] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}
